import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { useEffect, useMemo, useState } from "react";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { useProfile } from "@/hooks/useAuth";
import { useAppStore, type DateRangePreset, type TxDateRange } from "@/store/useAppStore";
import { useIsSolo } from "@/hooks/useIsSolo";
import { fetchAllAccounts, fetchCategories, fetchTransactionsRange, fetchMembers, deleteTransaction, type MonthRange } from "@/lib/queries";
import {
  fetchCcPurchasesRange,
  deleteCcPurchase,
  fetchCcPaymentsOnAccountInfo,
} from "@/lib/cc";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Lock, ArrowLeftRight, Pencil, Handshake, X, Scale, Trash2, CreditCard, ArrowDownNarrowWide, ArrowUpNarrowWide, Bot, RotateCcw, Search, ListChecks, Unlink } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { toastError } from "@/lib/errors";
import { TransactionFormDialog, type EditingTransaction } from "@/components/TransactionFormDialog";
import { TransferDialog } from "@/components/TransferDialog";
import { IouDialog } from "@/components/IouDialog";
import { RecurringBadge } from "@/components/RecurringInbox";
import { CcPurchaseDetailDialog } from "@/components/CcPurchaseDetailDialog";
import { CcRefundDialog } from "@/components/CcRefundDialog";
import { CategorizeMineDialog } from "@/components/CategorizeMineDialog";
import { BulkCategorizeDialog } from "@/components/BulkCategorizeDialog";
import { buildBulkLines } from "@/lib/bulkCategorize";
import { useFormatCurrency } from "@/hooks/useFormatCurrency";
import { accountTypeIcon, accountTypeLabel } from "@/lib/accountDisplay";
import { isAdjustCategory } from "@/lib/categories";
import { AccountFilterCombobox } from "@/components/AccountFilterCombobox";
import { allAccountsQueryKey } from "@/lib/authScopedQueryKeys";
import {
  fetchBankRefundRemaining,
  fetchBankRefundLinksFor,
  fetchHeadersByIds,
  fetchStatementVerificationLabels,
} from "@/lib/statementsData";
import { formatVerificationBadge, isMarkableForVerification, unmarkVerifiedByStatement } from "@/lib/statementVerify";
import { fetchCcManualImputations, unapplyCcPayment } from "@/lib/ccImpute";
import { ccPaymentActions, isHouseholdToHousehold, manualCountByPayment } from "@/lib/ccPaymentActions";
import { matchesTypeChips, type TypeChip } from "@/lib/movementKinds";
import { bankDescriptionToShow } from "@/lib/transactions";

const PAGE_SIZE = 200;

const fmtCreated = (iso: string) =>
  new Intl.DateTimeFormat("es", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }).format(new Date(iso));

const normalizeText = (s: string) =>
  s.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();

const txSearchSchema = z.object({
  cat: fallback(z.string(), "").default(""),
  // claridad-hogar V1: el link "ver los sin verificar" de Cuentas claras abre Movimientos ya
  // filtrado (cuenta + rango + "Sin verificar"). Todos opcionales: sin ellos, todo como antes.
  unverified: fallback(z.boolean(), false).default(false),
  account: fallback(z.string(), "").default(""),
  from: fallback(z.string(), "").default(""),
  to: fallback(z.string(), "").default(""),
});

export const Route = createFileRoute("/_app/transactions")({
  validateSearch: zodValidator(txSearchSchema),
  component: TxPage,
});

const PRESET_LABEL: Record<DateRangePreset, string> = {
  "this-week": "Semana actual",
  "last-week": "Semana anterior",
  "this-month": "Mes actual",
  "last-month": "Mes anterior",
  "this-year": "Año actual",
  "last-year": "Año anterior",
  "all-time": "Todos los tiempos",
  custom: "Personalizado…",
};

const fmtDate = (d: Date) => d.toISOString().slice(0, 10);

function resolveRange(r: TxDateRange): MonthRange | null {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth(); // 0-indexed
  const d = now.getUTCDate();
  // Lunes como primer día de la semana
  const dow = (now.getUTCDay() + 6) % 7;
  switch (r.preset) {
    case "this-week": {
      const start = new Date(Date.UTC(y, m, d - dow));
      const end = new Date(Date.UTC(y, m, d - dow + 6));
      return { startDate: fmtDate(start), endDate: fmtDate(end) };
    }
    case "last-week": {
      const start = new Date(Date.UTC(y, m, d - dow - 7));
      const end = new Date(Date.UTC(y, m, d - dow - 1));
      return { startDate: fmtDate(start), endDate: fmtDate(end) };
    }
    case "this-month": {
      const start = new Date(Date.UTC(y, m, 1));
      const end = new Date(Date.UTC(y, m + 1, 0));
      return { startDate: fmtDate(start), endDate: fmtDate(end) };
    }
    case "last-month": {
      const start = new Date(Date.UTC(y, m - 1, 1));
      const end = new Date(Date.UTC(y, m, 0));
      return { startDate: fmtDate(start), endDate: fmtDate(end) };
    }
    case "this-year":
      return { startDate: `${y}-01-01`, endDate: `${y}-12-31` };
    case "last-year":
      return { startDate: `${y - 1}-01-01`, endDate: `${y - 1}-12-31` };
    case "all-time":
      return null;
    case "custom":
      if (r.customFrom && r.customTo) return { startDate: r.customFrom, endDate: r.customTo };
      return null;
  }
}

function TxPage() {
  const formatCurrency = useFormatCurrency();
  const navigate = useNavigate();
  const { profile } = useProfile();
  const hh = profile?.household_id;
  const { activeCurrency, viewMode, txDateRange, setTxDateRange } = useAppStore();
  const { isSolo } = useIsSolo();
  const qc = useQueryClient();
  const search = Route.useSearch();
  const initialCat = search.cat;
  const [open, setOpen] = useState(false);
  const [openTransfer, setOpenTransfer] = useState(false);
  const [openIou, setOpenIou] = useState(false);
  const [editing, setEditing] = useState<EditingTransaction | null>(null);
  const [deleting, setDeleting] = useState<{ id: string; description: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // control-hogar P1: "Quitar marca del resumen" y "Desaplicar" (los dos con confirmación).
  const [unmarking, setUnmarking] = useState<{ headerIds: string[]; purchaseIds: string[]; description: string } | null>(null);
  const [unapplying, setUnapplying] = useState<{ id: string; description: string } | null>(null);
  const [isUndoing, setIsUndoing] = useState(false);
  const [ccDetail, setCcDetail] = useState<any | null>(null);
  const [refunding, setRefunding] = useState<any | null>(null);
  const [categorizingMine, setCategorizingMine] = useState<{
    header: any;
    ownLines: any[];
    accountName: string;
    accountCurrency: string;
    isIncome: boolean;
  } | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [filterAccountId, setFilterAccountId] = useState<string>(search.account || "all");
  const [filterCategoryId, setFilterCategoryId] = useState<string>(
    initialCat === "none" ? "none" : (initialCat || "all"),
  );
  // Multi-select chips de tipo. Vacío = todos.
  const [typeChips, setTypeChips] = useState<Set<TypeChip>>(new Set());
  type SourceChip = "web" | "agent_telegram" | "agent_whatsapp";
  const [sourceChips, setSourceChips] = useState<Set<SourceChip>>(new Set());
  // V1b: filtro "Sin verificar" — sólo entre lo que PUEDE venir de un resumen (isMarkable);
  // nunca iou (Decisión 5).
  const [onlyUnverified, setOnlyUnverified] = useState<boolean>(search.unverified === true);
  // El link trae su propio rango (la fecha de lo sin verificar): se aplica una vez por link.
  const linkFrom = search.from;
  const linkTo = search.to;
  useEffect(() => {
    if (!linkFrom || !linkTo) return;
    setTxDateRange({ preset: "custom", customFrom: linkFrom, customTo: linkTo });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkFrom, linkTo]);
  const [sortDir, setSortDir] = useState<"desc" | "asc">("desc");
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q), 200);
    return () => clearTimeout(t);
  }, [q]);

  const range = useMemo(() => resolveRange(txDateRange), [txDateRange]);
  // Clave estable para que tanstack-query agrupe páginas por rango.
  const rangeKey = range ? `${range.startDate}_${range.endDate}` : "all-time";

  const txQ = useInfiniteQuery({
    queryKey: ["tx-range", hh, rangeKey],
    enabled: !!hh,
    initialPageParam: 0,
    queryFn: ({ pageParam }) => fetchTransactionsRange(hh!, range, PAGE_SIZE, pageParam as number),
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((s, p) => s + p.headers.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
  });
  const ccQ = useQuery({
    queryKey: ["cc-purchases-range", hh, rangeKey],
    enabled: !!hh,
    queryFn: () => fetchCcPurchasesRange(hh!, range),
  });
  // Con búsqueda activa, la paginación no puede mentir: traer todo el rango
  // antes de poder decir "sin resultados".
  const searching = qDebounced.trim() !== "";
  useEffect(() => {
    if (!searching) return;
    if (txQ.hasNextPage && !txQ.isFetchingNextPage) {
      txQ.fetchNextPage();
    }
  }, [searching, txQ.hasNextPage, txQ.isFetchingNextPage, txQ.fetchNextPage]);
  const accQ = useQuery({
    queryKey: allAccountsQueryKey(hh ?? "", profile?.id ?? ""),
    queryFn: () => fetchAllAccounts(hh!),
    enabled: !!hh && !!profile?.id,
  });
  const catQ = useQuery({ queryKey: ["categories", hh], queryFn: () => fetchCategories(hh!), enabled: !!hh });
  const memQ = useQuery({ queryKey: ["members", hh], queryFn: () => fetchMembers(hh!), enabled: !!hh });

  const allHeaders = useMemo(
    () => (txQ.data?.pages ?? []).flatMap((p) => p.headers),
    [txQ.data],
  );
  const allLines = useMemo(
    () => (txQ.data?.pages ?? []).flatMap((p) => p.lines),
    [txQ.data],
  );
  const totalHeadersServer = txQ.data?.pages?.[0]?.total ?? 0;

  // P1: pagos de tarjeta a cuenta. El saldo sin aplicar y si el pago tiene aplicaciones sale de un RPC que sólo
  // devuelve totales (una aplicación sobre una cuota privada del otro miembro no la veo con la RLS).
  const ccPaymentIds = useMemo(
    () =>
      allHeaders
        .filter((h: any) => h.type === "cc_payment")
        .map((h: any) => h.id as string)
        .sort(),
    [allHeaders],
  );
  const onAccountQ = useQuery({
    queryKey: ["cc-payments-on-account", hh, profile?.id, ccPaymentIds],
    queryFn: () => fetchCcPaymentsOnAccountInfo(ccPaymentIds),
    enabled: !!hh && !!profile?.id && ccPaymentIds.length > 0,
  });

  // Synthetic "headers" para compras con TC (motor cc_purchases)
  const ccPurchases = ccQ.data?.purchases ?? [];
  const ccCharges = ccQ.data?.charges ?? [];
  const chargesByPurchase = useMemo(() => {
    const m = new Map<string, any[]>();
    ccCharges.forEach((c: any) => {
      const arr = m.get(c.purchase_id) ?? [];
      arr.push(c);
      m.set(c.purchase_id, arr);
    });
    return m;
  }, [ccCharges]);
  // R1: "Reembolso de <compra>" en un reembolso de TC (refund_of_purchase_id ya existe, sin SQL
  // nueva). Si la original quedó fuera del rango cargado, simplemente no se muestra el detalle.
  const ccPurchasesById = useMemo(
    () => new Map((ccPurchases as any[]).map((p: any) => [p.id as string, p])),
    [ccPurchases],
  );
  const ccAsHeaders = useMemo(() => ccPurchases.map((p: any) => ({
    __cc: true,
    id: `cc:${p.id}`,
    purchase_id: p.id,
    type: "cc_purchase" as const,
    date: p.purchase_date,
    created_at: p.created_at,
    created_by: p.created_by,
    account_id: p.account_id,
    related_account_id: null,
    description: p.description,
    total_amount: p.total_amount,
    is_locked: p.is_locked,
    installments_count: p.installments_count,
    visibility: p.visibility,
    owner_id: p.owner_id,
    category_id: p.category_id,
    currency: p.currency,
    is_refund: p.is_refund,
    refund_of_purchase_id: p.refund_of_purchase_id,
    // M2: "Lo pagó un tercero" (`refund_kind`) — `select("*")` ya lo trae.
    refund_kind: p.refund_kind,
    // V1b: `cc_purchases` se lee con `select("*")` (fetchCcPurchasesRange) — ya trae `verified_at`.
    verified_at: p.verified_at,
  })), [ccPurchases]);

  // (totalHeadersServer y ccPurchases se siguen usando para otras métricas)
  void totalHeadersServer;

  const accMap = new Map((accQ.data ?? []).map((a) => [a.id, a]));
  const catMap = new Map((catQ.data ?? []).map((c) => [c.id, c]));
  const memMap = new Map((memQ.data ?? []).map((m) => [m.id, m]));
  const adjustIds = useMemo(
    () => new Set((catQ.data ?? []).filter((c) => isAdjustCategory(c)).map((c) => c.id)),
    [catQ.data],
  );
  const linesByHeader = new Map<string, any[]>();
  allLines.forEach((l: any) => {
    const arr = linesByHeader.get(l.header_id) ?? [];
    arr.push(l);
    linesByHeader.set(l.header_id, arr);
  });

  const myId = profile?.id;

  // "Categorizar en grupo": mis líneas sin categoría del rango cargado, sin depender
  // de los filtros de la pantalla ni del modo Hogar/Personal (mezcla mis líneas
  // compartidas y privadas; el diálogo las separa por grupo).
  const bulkLines = useMemo(() => {
    const currencyByAccount = new Map((accQ.data ?? []).map((a: any) => [a.id, a.currency as string]));
    return buildBulkLines({
      headers: allHeaders,
      lines: allLines,
      myId,
      currencyOf: (h) => (h as any).currency ?? currencyByAccount.get(h.account_id ?? "") ?? activeCurrency,
    });
  }, [allHeaders, allLines, accQ.data, myId, activeCurrency]);

  const mergedHeaders = useMemo(
    () => [...allHeaders, ...ccAsHeaders].sort((a: any, b: any) => {
      const dir = sortDir === "desc" ? 1 : -1;
      if (a.date === b.date) {
        return dir * (b.created_at ?? "").localeCompare(a.created_at ?? "");
      }
      return dir * (b.date ?? "").localeCompare(a.date ?? "");
    }),
    [allHeaders, ccAsHeaders, sortDir],
  );

  // R1: reembolsos de cuenta vinculados a su compra original.
  // "Reembolsado $X de $Y" en la original: remaining sale de bank_refund_remaining (DEFINER, fuera
  // de RLS — un reembolso vinculado privado del otro miembro no se vería con una suma directa,
  // familia #158). Solo se pide para headers 'regular' no-reembolso ya cargados en esta página.
  const originalCandidateIds = useMemo(
    () =>
      allHeaders
        .filter((h: any) => h.type === "regular" && !h.is_refund)
        .map((h: any) => h.id as string)
        .sort(),
    [allHeaders],
  );
  const refundRemainingQ = useQuery({
    queryKey: ["bank-refund-remaining", hh, originalCandidateIds],
    queryFn: () => fetchBankRefundRemaining(originalCandidateIds),
    enabled: !!hh && originalCandidateIds.length > 0,
  });

  // Rebote #170 ronda 3, FIX 2: el vínculo real ya no viaja en `h.refund_of_header_id` (se sacó del
  // select de `fetchTransactionsRange` — familia #164). Se resuelve con `bank_refund_link_for`, que
  // sólo devuelve el id si la original es visible para quien mira esta página.
  const refundHeaderIds = useMemo(
    () =>
      allHeaders
        .filter((h: any) => h.is_refund)
        .map((h: any) => h.id as string)
        .sort(),
    [allHeaders],
  );
  const refundLinksQ = useQuery({
    queryKey: ["bank-refund-links-for", hh, refundHeaderIds],
    queryFn: () => fetchBankRefundLinksFor(refundHeaderIds),
    enabled: !!hh && refundHeaderIds.length > 0,
  });

  // "Reembolso de <compra> · <fecha> · parcial N% | total" en el reembolso: la original puede estar
  // fuera del rango/página cargada, así que las que faltan se traen aparte (RLS ya las filtra igual).
  const refundOriginalIds = useMemo(
    () => [...new Set([...(refundLinksQ.data?.values() ?? [])])],
    [refundLinksQ.data],
  );
  const missingRefundOriginalIds = useMemo(
    () => refundOriginalIds.filter((id) => !allHeaders.some((h: any) => h.id === id)),
    [refundOriginalIds, allHeaders],
  );
  const refundOriginalsQ = useQuery({
    queryKey: ["bank-refund-originals", hh, missingRefundOriginalIds],
    queryFn: () => fetchHeadersByIds(missingRefundOriginalIds),
    enabled: !!hh && missingRefundOriginalIds.length > 0,
  });
  const refundOriginalById = useMemo(() => {
    const map = new Map<string, { date: string; description: string | null; total_amount: number }>();
    allHeaders.forEach((h: any) => {
      if (!h.is_refund) map.set(h.id, { date: h.date, description: h.description, total_amount: Number(h.total_amount) });
    });
    (refundOriginalsQ.data ?? new Map()).forEach((v, k) => map.set(k, v));
    return map;
  }, [allHeaders, refundOriginalsQ.data]);

  const verifyHeaderIds = useMemo(
    () =>
      allHeaders
        .filter((h: any) => isMarkableForVerification(h) && !!h.verified_at)
        .map((h: any) => h.id as string)
        .sort(),
    [allHeaders],
  );
  const verifyPurchaseIds = useMemo(
    () => ccPurchases.filter((p: any) => !!p.verified_at).map((p: any) => p.id as string).sort(),
    [ccPurchases],
  );
  // V1b: etiqueta "✓ resumen <mes> · <cuenta>" de lo ya marcado — sólo se pide para lo cargado en
  // esta página (mismo patrón que refundRemainingQ/refundLinksQ). Si falla, el badge en sí sigue
  // mostrándose ("✓ resumen" a secas) — `formatVerificationBadge` funciona con `undefined`.
  const verifyLabelsQ = useQuery({
    queryKey: ["statement-verification-labels", hh, verifyHeaderIds, verifyPurchaseIds],
    queryFn: () => fetchStatementVerificationLabels(verifyHeaderIds, verifyPurchaseIds),
    enabled: !!hh && (verifyHeaderIds.length > 0 || verifyPurchaseIds.length > 0),
  });

  // control-hogar P1: imputaciones MANUALES por pago (para distinguirlas de las de "Aplicar pagos", que
  // se deshacen por período desde la tarjeta). `get_cc_manual_imputations` es por tarjeta y exige que la
  // vea: sólo se piden las tarjetas visibles. Si cualquiera falla, la query entera falla y la fila no
  // afirma nada (#129): ni ofrece "Desaplicar" ni dice que son automáticas.
  const manualCardIds = useMemo(() => {
    const visible = new Set((accQ.data ?? []).map((a: any) => a.id as string));
    return [
      ...new Set(
        allHeaders
          .filter((h: any) => h.type === "cc_payment" && h.related_account_id && visible.has(h.related_account_id))
          .map((h: any) => h.related_account_id as string),
      ),
    ].sort();
  }, [allHeaders, accQ.data]);
  const manualQ = useQuery({
    queryKey: ["cc-manual-imputations-by-card", hh, profile?.id, manualCardIds],
    queryFn: async () => manualCountByPayment(await Promise.all(manualCardIds.map((c) => fetchCcManualImputations(c)))),
    enabled: !!hh && !!profile?.id && manualCardIds.length > 0,
  });

  const privacyHeaders = mergedHeaders.filter((h: any) => {
    if (h.__cc) {
      // visibility/owner están en la propia compra
      if (h.created_by === myId) return true;
      if (h.visibility === "shared") return true;
      return h.owner_id === myId;
    }
    if (h.type === "cc_payment") {
      // Pago de TC (sin líneas): visible si lo creé yo o si alguna de las
      // cuentas involucradas (origen o tarjeta) me es visible. Defensa en
      // profundidad que refleja la RLS D3. Una cuenta del peer privada no
      // está en accMap → no aporta visibilidad.
      if (h.created_by === myId) return true;
      const visibleAcc = (a: any) => !!a && (a.is_shared !== false || a.owner_id === myId);
      return (
        visibleAcc(accMap.get(h.account_id)) ||
        visibleAcc(h.related_account_id ? accMap.get(h.related_account_id) : null)
      );
    }
    const lines = linesByHeader.get(h.id) ?? [];
    const acc = accMap.get(h.account_id);
    const accIsMineOrShared = !acc?.owner_id || acc.owner_id === myId;
    const hasMineOrShared = lines.some((l) => l.visibility === "shared" || l.owner_id === myId);
    if (h.created_by === myId) return true;
    if (accIsMineOrShared) return true;
    return hasMineOrShared;
  });

  const visibleHeaders = privacyHeaders.filter((h: any) => {
    if (isSolo) return true;
    if (h.__cc) {
      // Evalúa la privacidad real sobre las cuotas (pueden haber sido
      // editadas a private/shared después de creada la compra).
      const charges = chargesByPurchase.get(h.purchase_id) ?? [];
      const visibleCharges = charges.filter(
        (c: any) => c.visibility === "shared" || c.owner_id === myId,
      );
      if (viewMode === "household") {
        return visibleCharges.some((c: any) => c.visibility === "shared");
      }
      return visibleCharges.some(
        (c: any) => c.visibility === "private" && c.owner_id === myId,
      );
    }
    if (h.type === "cc_payment") {
      // D3: el pago sigue la visibilidad de las cuentas (origen + tarjeta).
      // HOGAR: alguna cuenta compartida involucrada.
      // PERSONAL: alguna cuenta privada mía involucrada.
      const src = accMap.get(h.account_id);
      const card = h.related_account_id ? accMap.get(h.related_account_id) : null;
      const isShared = (a: any) => !!a && a.is_shared !== false;
      const isMinePrivate = (a: any) => !!a && a.is_shared === false && a.owner_id === myId;
      if (viewMode === "household") return isShared(src) || isShared(card);
      return isMinePrivate(src) || isMinePrivate(card);
    }
    if (h.type === "transfer") return viewMode === "household";
    if (h.type === "iou") {
      // IOUs son acuerdos del hogar — solo visibles en modo Hogar.
      // El usuario los gestiona desde "Cuentas Claras" (/iou).
      return viewMode === "household";
    }
    const lines = linesByHeader.get(h.id) ?? [];
    if (viewMode === "personal") {
      return lines.some((l) => l.visibility === "private" && l.owner_id === myId);
    }
    // HOGAR estricto: el header solo aparece si TODAS sus líneas son shared.
    // Si alguna línea es privada, el header se oculta del modo Hogar.
    return lines.length > 0 && lines.every((l) => l.visibility === "shared");
  });

  // Cuentas visibles en el combobox: NO reacciona al toggle Hogar/Personal,
  // pero excluye permanentemente cuentas privadas del peer.
  const visibleAccountsForFilter = useMemo(() => {
    const all = accQ.data ?? [];
    return all.filter((a: any) => a.is_shared !== false || a.owner_id === myId);
  }, [accQ.data, myId]);

  // Map de id → nombre para pasar al combobox de cuentas
  const memberNamesMap = useMemo(() => {
    const m = new Map<string, string>();
    (memQ.data ?? []).forEach((mem: any) => m.set(mem.id, mem.display_name));
    return m;
  }, [memQ.data]);

  const filterableCategories = useMemo(() => {
    const all = (catQ.data ?? []).filter((c: any) => !c.parent_id); // excluir subcategorías
    if (isSolo) {
      return all.slice().sort((a: any, b: any) => a.name.localeCompare(b.name));
    }
    if (viewMode === "household") {
      return all
        .filter((c: any) => c.visibility === "shared")
        .slice()
        .sort((a: any, b: any) => a.name.localeCompare(b.name));
    }
    // personal: solo las categorías privadas del usuario (por diseño nunca usan categorías del hogar)
    if (!myId) return [];
    return all
      .filter((c: any) => c.visibility === "private" && c.owner_id === myId)
      .slice()
      .sort((a: any, b: any) => a.name.localeCompare(b.name));
  }, [catQ.data, isSolo, viewMode, myId]);

  // Reset de filtros si el valor seleccionado deja de ser visible al cambiar viewMode.
  useEffect(() => {
    // Guard: no resetear durante carga (evita falso positivo al togglear viewMode).
    if (!catQ.data || (!isSolo && !myId)) return;
    if (filterCategoryId === "all" || filterCategoryId === "none") return;
    if (!filterableCategories.some((c: any) => c.id === filterCategoryId)) {
      setFilterCategoryId("all");
    }
  }, [filterableCategories, filterCategoryId, catQ.data, myId, isSolo]);
  useEffect(() => {
    if (filterAccountId === "all") return;
    if (!visibleAccountsForFilter.some((a: any) => a.id === filterAccountId)) {
      setFilterAccountId("all");
    }
  }, [visibleAccountsForFilter, filterAccountId]);

  // IDs que coinciden con el filtro activo: la categoría seleccionada más todas sus subcategorías.
  // El dropdown solo muestra categorías padre (!parent_id), así que un nivel de hijos es suficiente.
  const childCatIds = useMemo(() => {
    if (filterCategoryId === "all" || filterCategoryId === "none") return new Set<string>();
    const ids = new Set<string>([filterCategoryId]);
    (catQ.data ?? []).forEach((c) => {
      if (c.parent_id === filterCategoryId) ids.add(c.id);
    });
    return ids;
  }, [filterCategoryId, catQ.data]);

  const filteredHeaders = useMemo(() => {
    return visibleHeaders.filter((h) => {
      if (filterAccountId !== "all") {
        const matchesAcc = h.account_id === filterAccountId || h.related_account_id === filterAccountId;
        if (!matchesAcc) return false;
      }
      if (!matchesTypeChips(typeChips, h as any, linesByHeader.get(h.id) ?? [], adjustIds, (cid) => catMap.get(cid)?.type)) return false;
      if (sourceChips.size > 0) {
        const src: string = (h as any).source ?? "web";
        const normalized: SourceChip =
          src === "agent_telegram" ? "agent_telegram" :
          src === "agent_whatsapp" ? "agent_whatsapp" :
          "web";
        if (!sourceChips.has(normalized)) return false;
      }
      if (onlyUnverified) {
        // V1b (Decisión 5): sólo lo que PUEDE venir de un resumen; iou nunca se ofrece acá (siempre
        // quedaría "sin verificar" — sería puro ruido, no un dato accionable).
        if (!isMarkableForVerification(h) || (h as any).verified_at) return false;
      }
      if (filterCategoryId !== "all") {
        if ((h as any).type === "iou") return false;
        if ((h as any).__cc) {
          const cid = (h as any).category_id ?? null;
          if (filterCategoryId === "none") {
            if (cid !== null) return false;
          } else {
            if (!childCatIds.has(cid ?? "")) return false;
          }
        } else {
          const lines = linesByHeader.get(h.id) ?? [];
          // Mismas líneas que la UI renderiza en la tarjeta (shared + mine).
          const matchableLines = lines.filter(
            (l) => l.visibility === "shared" || l.owner_id === myId,
          );
          if (filterCategoryId === "none") {
            if (!matchableLines.some((l) => !l.category_id)) return false;
          } else {
            if (!matchableLines.some((l) => childCatIds.has(l.category_id ?? ""))) return false;
          }
        }
      }
      return true;
    });
  }, [visibleHeaders, filterAccountId, typeChips, sourceChips, onlyUnverified, filterCategoryId, childCatIds, linesByHeader, myId, adjustIds, catMap]);

  // Buscador de texto: se aplica DESPUÉS de todos los filtros de arriba, para
  // que el acumulado (totalsByCurrency) y la lista siempre muestren lo mismo.
  const searchedHeaders = useMemo(() => {
    const qNorm = normalizeText(qDebounced.trim());
    if (!qNorm) return filteredHeaders;
    const qDigits = qDebounced.replace(/[^\d.]/g, "");
    return filteredHeaders.filter((h: any) => {
      const parts: string[] = [];
      if (h.description) parts.push(h.description);
      const acc = accMap.get(h.account_id);
      if (acc?.name) parts.push(acc.name);
      if (h.related_account_id) {
        const relAcc = accMap.get(h.related_account_id);
        if (relAcc?.name) parts.push(relAcc.name);
      }
      if (h.__cc) {
        if (h.category_id) {
          const c = catMap.get(h.category_id);
          if (c?.name) parts.push(c.name);
        }
      } else {
        const lines = linesByHeader.get(h.id) ?? [];
        const matchableLines = lines.filter((l: any) => l.visibility === "shared" || l.owner_id === myId);
        matchableLines.forEach((l: any) => {
          const c = catMap.get(l.category_id ?? "");
          if (c?.name) parts.push(c.name);
        });
      }
      if (normalizeText(parts.join(" ")).includes(qNorm)) return true;
      if (qDigits) {
        const amountStr = Math.abs(Number(h.total_amount) || 0).toFixed(2);
        if (amountStr.includes(qDigits)) return true;
      }
      return false;
    });
  }, [filteredHeaders, qDebounced, accMap, catMap, linesByHeader, myId]);

  // Acumulado por moneda. Excluye transfers, iou, ajustes.
  // Cuando hay filtro de categoría activo, solo suma las líneas del árbol filtrado
  // para que el total coincida con el valor que muestra el dashboard para esa categoría.
  const totalsByCurrency = useMemo(() => {
    const map = new Map<string, { income: number; expense: number }>();
    const bump = (cur: string, kind: "income" | "expense", amount: number) => {
      const cur2 = cur || activeCurrency;
      const e = map.get(cur2) ?? { income: 0, expense: 0 };
      e[kind] += amount;
      map.set(cur2, e);
    };
    const catFilterActive = filterCategoryId !== "all" && filterCategoryId !== "none";
    for (const h of searchedHeaders) {
      if (h.type === "transfer" || (h.type as any) === "iou" || (h.type as any) === "cc_payment") continue;
      if ((h as any).__cc) {
        const cur = (h as any).currency || activeCurrency;
        bump(cur, "expense", Number((h as any).total_amount));
        continue;
      }
      const acc = accMap.get(h.account_id);
      const cur = acc?.currency ?? activeCurrency;
      const lines = linesByHeader.get(h.id) ?? [];
      const visible = lines.filter((l) => l.visibility === "shared" || l.owner_id === myId);
      const sign = (h as any).is_refund ? -1 : 1;
      for (const l of visible) {
        if (adjustIds.has(l.category_id ?? "")) continue;
        if (catFilterActive && !childCatIds.has(l.category_id ?? "")) continue;
        const c = catMap.get(l.category_id ?? "");
        if (c?.type === "income") bump(cur, "income", Number(l.amount));
        else if (c?.type === "expense") bump(cur, "expense", sign * Number(l.amount));
      }
    }
    return Array.from(map.entries()).map(([currency, v]) => ({ currency, ...v, net: v.income - v.expense }));
  }, [searchedHeaders, accMap, linesByHeader, catMap, myId, activeCurrency, adjustIds, filterCategoryId, childCatIds]);

  const filtersActive =
    filterAccountId !== "all" || filterCategoryId !== "all" || typeChips.size > 0 || sourceChips.size > 0 || onlyUnverified || txDateRange.preset !== "this-month" || q.trim() !== "";
  function clearFilters() {
    setFilterAccountId("all");
    setFilterCategoryId("all");
    setTypeChips(new Set());
    setSourceChips(new Set());
    setOnlyUnverified(false);
    setTxDateRange({ preset: "this-month" });
    setQ("");
    setQDebounced("");
  }
  function toggleChip(chip: TypeChip) {
    setTypeChips((prev) => {
      const next = new Set(prev);
      if (next.has(chip)) next.delete(chip);
      else next.add(chip);
      return next;
    });
  }

  const showCustom = txDateRange.preset === "custom";
  const rangeLabel = range
    ? `${range.startDate} → ${range.endDate}`
    : "Histórico completo";

  function toggleSourceChip(chip: SourceChip) {
    setSourceChips((prev) => {
      const next = new Set(prev);
      if (next.has(chip)) next.delete(chip);
      else next.add(chip);
      return next;
    });
  }

  const CHIPS: { key: TypeChip; label: string; icon?: any }[] = [
    { key: "income", label: "Ingreso" },
    { key: "expense", label: "Egreso" },
    { key: "adjustment", label: "Ajuste" },
    { key: "transfer", label: "Traspaso" },
    ...(!isSolo ? [{ key: "iou" as TypeChip, label: "Préstamo" }] : []),
    { key: "cc_purchase", label: "Compra con tarjeta" },
    { key: "cc_payment", label: "Pago de tarjeta" },
    { key: "refund", label: "Reembolso" },
    { key: "third_party", label: "Pagado por tercero" },
  ];

  const SOURCE_CHIPS: { key: SourceChip; label: string }[] = [
    { key: "web", label: "Manual" },
    { key: "agent_telegram", label: "Telegram" },
    { key: "agent_whatsapp", label: "WhatsApp" },
  ];

  function SourceBadge({ source }: { source?: string }) {
    if (!source || source === "web" || source === "recurring" || source === "import") return null;
    if (source === "agent_telegram") return (
      <span className="rounded px-2 py-0.5 text-[10px] bg-blue-500/15 text-blue-500 inline-flex items-center gap-1">
        <Bot className="h-3 w-3" /> Telegram
      </span>
    );
    if (source === "agent_whatsapp") return (
      <span className="rounded px-2 py-0.5 text-[10px] bg-green-600/15 text-green-600 inline-flex items-center gap-1">
        <Bot className="h-3 w-3" /> WhatsApp
      </span>
    );
    return (
      <span className="rounded px-2 py-0.5 text-[10px] bg-muted text-muted-foreground inline-flex items-center gap-1">
        <Bot className="h-3 w-3" /> Bot
      </span>
    );
  }

  // V1b: badge "✓ resumen <mes> · <cuenta>" / "✓ resumen" — sólo se llama cuando `verified_at` ya
  // es truthy (el dato real); `label` puede venir `undefined` mientras carga o si la RPC de
  // etiquetas falla, y el badge igual se muestra (#110: nunca desaparece por un error de red).
  function VerifiedBadge({
    label,
    target,
  }: {
    label?: { periodYear: number | null; periodMonth: number | null; accountName: string | null };
    /** Qué se desmarca si se toca la ✕ (un header o una compra de tarjeta) y cómo se nombra en el diálogo. */
    target?: { headerId?: string; purchaseId?: string; description: string };
  }) {
    return (
      <span
        className="rounded px-2 py-0.5 text-[10px] bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1"
        title="Verificado por un resumen conciliado"
      >
        {formatVerificationBadge(label ?? null)}
        {target && (
          <button
            type="button"
            className="ml-0.5 rounded hover:bg-emerald-500/25"
            title="Quitar marca del resumen"
            aria-label="Quitar marca del resumen"
            onClick={() =>
              setUnmarking({
                headerIds: target.headerId ? [target.headerId] : [],
                purchaseIds: target.purchaseId ? [target.purchaseId] : [],
                description: target.description,
              })
            }
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </span>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold">Movimientos</h1>
          <RecurringBadge />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setOpenTransfer(true)}>
            <ArrowLeftRight className="mr-2 h-4 w-4" /> Traspaso
          </Button>
          {!isSolo && (
            <Button variant="outline" onClick={() => setOpenIou(true)}>
              <Handshake className="mr-2 h-4 w-4" /> Préstamo / Deuda
            </Button>
          )}
          <Button className="w-full sm:w-auto" onClick={() => { setEditing(null); setOpen(true); }}><Plus className="mr-2 h-4 w-4" /> Nuevo</Button>
        </div>
      </div>

      {/* Barra de filtros + acumulado para conciliación */}
      <Card className="p-3 space-y-3">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col gap-1 min-w-[180px]">
            <label className="text-[11px] text-muted-foreground">Rango de fechas</label>
            <Select
              value={txDateRange.preset}
              onValueChange={(v) => setTxDateRange({ ...txDateRange, preset: v as DateRangePreset })}
            >
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(Object.keys(PRESET_LABEL) as DateRangePreset[]).map((p) => (
                  <SelectItem key={p} value={p}>{PRESET_LABEL[p]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {showCustom && (
            <>
              <div className="flex flex-col gap-1">
                <label className="text-[11px] text-muted-foreground">Desde</label>
                <Input
                  type="date"
                  className="h-9 w-[150px]"
                  value={txDateRange.customFrom ?? ""}
                  onChange={(e) => setTxDateRange({ ...txDateRange, customFrom: e.target.value })}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[11px] text-muted-foreground">Hasta</label>
                <Input
                  type="date"
                  className="h-9 w-[150px]"
                  value={txDateRange.customTo ?? ""}
                  onChange={(e) => setTxDateRange({ ...txDateRange, customTo: e.target.value })}
                />
              </div>
            </>
          )}
          <div className="flex flex-col gap-1 min-w-[220px] flex-1">
            <label className="text-[11px] text-muted-foreground">Cuenta</label>
            <AccountFilterCombobox
              value={filterAccountId}
              onChange={setFilterAccountId}
              accounts={visibleAccountsForFilter as any}
              myId={myId}
              memberNames={memberNamesMap}
            />
          </div>
          <div className="flex flex-col gap-1 min-w-[200px]">
            <label className="text-[11px] text-muted-foreground">Categoría</label>
            <Select value={filterCategoryId} onValueChange={setFilterCategoryId}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las categorías</SelectItem>
                <SelectItem value="none">⚠ Sin categoría</SelectItem>
                {filterableCategories.map((c: any) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {bulkLines.length > 0 && (
            <Button variant="outline" className="h-9" onClick={() => setBulkOpen(true)}>
              <ListChecks className="mr-2 h-4 w-4" /> Categorizar en grupo ({bulkLines.length})
            </Button>
          )}
          <div className="flex flex-col gap-1 min-w-[240px] flex-1">
            <label className="text-[11px] text-muted-foreground">Buscar</label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="h-9 pl-8 pr-8"
                placeholder="Buscar por descripción, cuenta, categoría o monto…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
              {q && (
                <button
                  type="button"
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => { setQ(""); setQDebounced(""); }}
                  title="Limpiar búsqueda"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
          {filtersActive && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="h-9">
              <X className="mr-1 h-3.5 w-3.5" /> Limpiar
            </Button>
          )}
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9"
            title={sortDir === "desc" ? "Más recientes primero (clic para invertir)" : "Más antiguos primero (clic para invertir)"}
            onClick={() => setSortDir((d) => (d === "desc" ? "asc" : "desc"))}
          >
            {sortDir === "desc" ? <ArrowDownNarrowWide className="h-4 w-4" /> : <ArrowUpNarrowWide className="h-4 w-4" />}
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] text-muted-foreground mr-1">Tipo:</span>
          {CHIPS.map((chip) => {
            const active = typeChips.has(chip.key);
            return (
              <button
                key={chip.key}
                type="button"
                onClick={() => toggleChip(chip.key)}
                className={
                  "rounded-full border px-2.5 py-0.5 text-[11px] transition " +
                  (active
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background hover:bg-muted")
                }
              >
                {chip.label}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] text-muted-foreground mr-1">Origen:</span>
          {SOURCE_CHIPS.map((chip) => {
            const active = sourceChips.has(chip.key);
            return (
              <button
                key={chip.key}
                type="button"
                onClick={() => toggleSourceChip(chip.key)}
                className={
                  "rounded-full border px-2.5 py-0.5 text-[11px] transition " +
                  (active
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background hover:bg-muted")
                }
              >
                {chip.label}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] text-muted-foreground mr-1">Verificación:</span>
          <button
            type="button"
            onClick={() => setOnlyUnverified((v) => !v)}
            className={
              "rounded-full border px-2.5 py-0.5 text-[11px] transition " +
              (onlyUnverified
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-background hover:bg-muted")
            }
          >
            Sin verificar
          </button>
        </div>
        <div className="border-t pt-2">
          {searching && (txQ.hasNextPage || txQ.isFetchingNextPage) && (
            <div className="mb-1 flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
              Buscando en todo el rango…
            </div>
          )}
          <div className="text-[11px] text-muted-foreground mb-1">
            {rangeLabel} · Mostrando {searchedHeaders.length} de {visibleHeaders.length} movimiento(s)
          </div>
          {totalsByCurrency.length === 0 ? (
            <div className="text-xs text-muted-foreground">Sin ingresos ni gastos en la selección.</div>
          ) : (
            <div className="grid gap-2 sm:grid-cols-3">
              {totalsByCurrency.map((t) => (
                <div key={t.currency} className="rounded-md border p-2 text-xs">
                  <div className="text-[10px] text-muted-foreground mb-1">{t.currency}</div>
                  <div className="flex items-center justify-between">
                    <span className="text-income">Ingresos</span>
                    <span className="font-semibold tabular-nums">{formatCurrency(t.income, t.currency)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-expense">Gastos</span>
                    <span className="font-semibold tabular-nums">{formatCurrency(t.expense, t.currency)}</span>
                  </div>
                  <div className="flex items-center justify-between border-t mt-1 pt-1">
                    <span className="text-muted-foreground">Neto</span>
                    <span className={"font-semibold tabular-nums " + (t.net >= 0 ? "text-income" : "text-expense")}>
                      {t.net >= 0 ? "+" : ""}{formatCurrency(t.net, t.currency)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      <Card className="divide-y">
        {searchedHeaders.length === 0 && !(searching && (txQ.hasNextPage || txQ.isFetchingNextPage)) && (
          <div className="p-8 text-center text-sm text-muted-foreground">
            {searching ? (
              <>
                Sin resultados para «{qDebounced.trim()}» en {rangeLabel}.
                <br />
                Probá ampliar el rango de fechas.
              </>
            ) : filtersActive ? (
              "No hay movimientos que coincidan con los filtros."
            ) : (
              "No hay movimientos en este rango."
            )}
          </div>
        )}
        {searchedHeaders.map((h) => {
          const acc = accMap.get(h.account_id);
          const relAcc = h.related_account_id ? accMap.get(h.related_account_id) : null;
          const allLinesH = linesByHeader.get(h.id) ?? [];
          const visibleLines = allLinesH.filter((l) => l.visibility === "shared" || l.owner_id === myId);
          const isPeerPrivate = h.created_by !== myId && visibleLines.length === 0;
          const ownerName = memMap.get(h.created_by)?.display_name ?? "tu pareja";
          const AccIcon = acc ? accountTypeIcon(acc.type) : null;
          const AccountChip = acc ? (
            <span className="inline-flex items-center gap-1 rounded border bg-muted/40 px-1.5 py-0.5 text-[10px] text-muted-foreground">
              {AccIcon && <AccIcon className="h-3 w-3" />}
              <span>{acc.name}</span>
              {!(acc as any).is_active && <span className="text-warning">(archivada)</span>}
            </span>
          ) : null;
          if ((h as any).__cc && (h as any).is_refund) {
            // Reembolso de TC: crédito sobre una compra. Baja deuda y gasto neto.
            const refundCurrency = (h as any).currency ?? activeCurrency;
            const credit = Math.abs(Number(h.total_amount));
            // R1: refund_of_purchase_id ya existe en cc_purchases (sin SQL nueva). Si la original
            // quedó fuera del rango cargado, no se muestra el detalle (no hay fetch extra para esto).
            const ccOrigId = (h as any).refund_of_purchase_id as string | null;
            const ccOrig = ccOrigId ? ccPurchasesById.get(ccOrigId) : null;
            const ccOrigTotal = ccOrig ? Number((ccOrig as any).total_amount) : 0;
            const ccPct = ccOrig && ccOrigTotal > 0 ? Math.round((credit / ccOrigTotal) * 100) : 0;
            return (
              <div key={h.id} className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-medium flex items-center gap-2">
                      <RotateCcw className="h-4 w-4 text-income" />
                      {h.description || "Reembolso de tarjeta"}
                      <span className="rounded px-2 py-0.5 text-[10px] bg-income/15 text-income">
                        Reembolso
                      </span>
                      {(h as any).verified_at && (
                        <VerifiedBadge label={verifyLabelsQ.data?.purchases.get((h as any).purchase_id)} target={{ purchaseId: (h as any).purchase_id, description: `Tarjeta · ${h.description || "(sin descripción)"} · ${h.date}` }} />
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                      <span>{h.date}</span>
                      {AccountChip}
                      {(h as any).visibility === "private" && <Lock className="h-3 w-3" />}
                    </div>
                    {ccOrig && (
                      <div className="mt-1 text-[11px] text-muted-foreground">
                        Reembolso de {(ccOrig as any).description || "(compra)"} · {(ccOrig as any).purchase_date} ·{" "}
                        {ccPct >= 100 ? "total" : `parcial ${ccPct}%`}
                      </div>
                    )}
                    {h.created_at && <div className="text-[10px] text-muted-foreground">Creado el {fmtCreated(h.created_at)}</div>}
                  </div>
                  <div className="text-right font-semibold text-income">
                    + {formatCurrency(credit, refundCurrency)}
                  </div>
                </div>
              </div>
            );
          }
          if ((h as any).__cc) {
            const purchaseId = (h as any).purchase_id as string;
            const installments = (h as any).installments_count as number;
            const charges = chargesByPurchase.get(purchaseId) ?? [];
            const paid = charges.reduce((s: number, c: any) => s + Number(c.amount_paid || 0), 0);
            const totalAmt = Number(h.total_amount);
            const remaining = Math.max(0, totalAmt - paid);
            const visibleCharges = charges.filter(
              (c: any) => c.visibility === "shared" || c.owner_id === myId,
            );
            const ccCatIds = new Set(
              visibleCharges
                .map((c: any) => c.category_id)
                .filter((x: any): x is string => !!x),
            );
            const ccCat = ccCatIds.size === 1 ? catMap.get([...ccCatIds][0] as string) : null;
            const ccMulti = ccCatIds.size > 1;
            const ccMissing = visibleCharges.some(
              (c: any) => c.owner_id === myId && !c.category_id,
            );
            const isSingleInstallment = installments === 1;
            return (
              <div key={h.id} className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-medium flex items-center gap-2">
                      <CreditCard className={"h-4 w-4 " + (isSingleInstallment ? "text-muted-foreground" : "")} />
                      {h.description || "(sin descripción)"}
                      <span
                        className={
                          "rounded px-2 py-0.5 text-[10px] " +
                          (isSingleInstallment
                            ? "bg-muted text-muted-foreground"
                            : "bg-[var(--cc-tag-bg)] text-[var(--cc-tag-text)]")
                        }
                      >
                        Tarjeta · {isSingleInstallment ? "1 pago" : `${installments} cuotas`}
                      </span>
                      {(h as any).verified_at && (
                        <VerifiedBadge label={verifyLabelsQ.data?.purchases.get((h as any).purchase_id)} target={{ purchaseId: (h as any).purchase_id, description: `Tarjeta · ${h.description || "(sin descripción)"} · ${h.date}` }} />
                      )}
                      {paid > 0 && (
                        <span className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] bg-muted text-muted-foreground">
                          <Lock className="h-3 w-3" /> Con pagos
                        </span>
                      )}
                      {ccMissing && (
                        <span className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] bg-[var(--warning-bg)] text-warning">
                          ⚠ Falta categoría
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                      <span>{h.date}</span>
                      {AccountChip}
                      {ccCat && (
                        <span className="inline-flex items-center gap-1">
                          <span className="inline-block h-2 w-2 rounded-full" style={{ background: ccCat.color }} />
                          {ccCat.name}
                        </span>
                      )}
                      {ccMulti && (
                        <span className="rounded px-1.5 py-0.5 text-[10px] bg-muted text-muted-foreground">
                          Múltiples categorías
                        </span>
                      )}
                      {(h as any).visibility === "private" && <Lock className="h-3 w-3" />}
                    </div>
                    {h.created_at && <div className="text-[10px] text-muted-foreground">Creado el {fmtCreated(h.created_at)}</div>}
                    <div className="mt-1 text-[11px] text-muted-foreground">
                      Pagado: {formatCurrency(paid, (h as any).currency ?? activeCurrency)} · Pendiente: <b>{formatCurrency(remaining, (h as any).currency ?? activeCurrency)}</b>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-right font-semibold">
                      {formatCurrency(totalAmt, (h as any).currency ?? activeCurrency)}
                    </div>
                    <Button size="icon" variant="ghost" title="Ver cuotas" onClick={() => setCcDetail(h)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="ghost" title="Registrar reembolso" onClick={() => setRefunding(h)}>
                      <RotateCcw className="h-4 w-4" />
                    </Button>
                    {h.created_by === myId && !(h as any).is_locked && (
                      <Button size="icon" variant="ghost" title="Eliminar compra"
                        onClick={() => setDeleting({ id: h.id, description: `Tarjeta · ${h.description || "(sin descripción)"}` })}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          }
          if (h.type === "cc_payment") {
            // Origen: si la cuenta no está en accMap es una cuenta privada del
            // peer → la RLS de accounts impide resolver su nombre. Lo mostramos
            // enmascarado como "Cuenta privada" (no fuga de privacidad).
            const sourceName = acc?.name ?? "Cuenta privada";
            const cardName = relAcc?.name ?? "Tarjeta";
            const payCurrency = acc?.currency ?? relAcc?.currency ?? activeCurrency;
            // P1: un pago sin aplicaciones es un pago A CUENTA. Sin la lectura (error o en curso) no se afirma
            // nada: ni "sin aplicar" ni Borrar (un `data ?? []` vacío no puede leerse como "no tiene").
            const payInfo = onAccountQ.data?.get(h.id);
            const onAccount = !!payInfo && payInfo.applications_count === 0;
            // control-hogar P1: qué se ofrece (tacho / Desaplicar / texto). Imputaciones manuales: sólo si la
            // tarjeta es visible y la lectura llegó (si no, `undefined` = no se afirma nada).
            const actions = ccPaymentActions({
              createdBy: h.created_by,
              myId,
              householdToHousehold: isHouseholdToHousehold(acc, relAcc),
              applicationsCount: payInfo?.applications_count,
              manualCount: manualQ.data && relAcc ? (manualQ.data.get(h.id) ?? 0) : undefined,
            });
            const partlyUnapplied = !!payInfo && payInfo.applications_count > 0 && payInfo.unapplied > 0.005;
            return (
              <div key={h.id} className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-medium flex items-center gap-2">
                      <CreditCard className="h-4 w-4" /> {onAccount ? "Pago a cuenta" : "Pago de tarjeta"}
                      <span className="rounded px-2 py-0.5 text-[10px] bg-[var(--cc-tag-bg)] text-[var(--cc-tag-text)]">
                        Tarjeta · pago
                      </span>
                      {(h as any).verified_at && <VerifiedBadge label={verifyLabelsQ.data?.headers.get(h.id)} target={{ headerId: h.id, description: `Pago de tarjeta · ${sourceName} → ${cardName} · ${h.date}` }} />}
                    </div>
                    <div className="text-xs text-muted-foreground">{h.date} · {sourceName} → {cardName}</div>
                    {h.created_at && <div className="text-[10px] text-muted-foreground">Creado el {fmtCreated(h.created_at)}</div>}
                    {h.description && h.description !== "Pago de tarjeta" && (
                      <div className="text-xs text-muted-foreground">{h.description}</div>
                    )}
                    {onAccount && (
                      <div className="mt-1 text-[11px] text-muted-foreground">
                        Pago a cuenta · sin aplicar{" "}
                        <b>{formatCurrency(payInfo!.unapplied, payCurrency)}</b>
                      </div>
                    )}
                    {partlyUnapplied && (
                      <div className="mt-1 text-[11px] text-muted-foreground">
                        Sin aplicar: <b>{formatCurrency(payInfo!.unapplied, payCurrency)}</b>
                      </div>
                    )}
                    {actions.hasApplications && (
                      <div className="mt-1 text-[11px] text-muted-foreground">
                        {actions.onlyAutomatic
                          ? "Aplicado con «Aplicar pagos»: se deshace por período desde la tarjeta."
                          : actions.canUnapply
                            ? "Para borrarlo, primero desaplicalo."
                            : "Tiene aplicaciones: para borrarlo hay que desaplicarlo primero."}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-right text-sm text-expense">
                      - {formatCurrency(Number(h.total_amount), payCurrency)}
                    </div>
                    {/* Tacho: sólo un pago SIN aplicaciones (con aplicaciones `delete_transaction` choca con la FK,
                        #135) y creado por mí o entre cuentas del hogar que veo (el RPC lo vuelve a exigir). */}
                    {actions.canUnapply && (
                      <Button size="icon" variant="ghost" title="Desaplicar el pago (imputaciones manuales)"
                        onClick={() => setUnapplying({ id: h.id, description: `Pago de tarjeta · ${sourceName} → ${cardName} · ${h.date}` })}>
                        <Unlink className="h-4 w-4" />
                      </Button>
                    )}
                    {actions.canDelete && (
                      <Button size="icon" variant="ghost" title={onAccount ? "Eliminar pago a cuenta" : "Eliminar pago"}
                        onClick={() => setDeleting({ id: h.id, description: `${onAccount ? "Pago a cuenta" : "Pago de tarjeta"} · ${sourceName} → ${cardName} · ${h.date}` })}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          }
          if (h.type === "transfer") {
            return (
              <div key={h.id} className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-medium flex items-center gap-2">
                      <ArrowLeftRight className="h-4 w-4" /> Traspaso entre cuentas
                      {(h as any).verified_at && <VerifiedBadge label={verifyLabelsQ.data?.headers.get(h.id)} target={{ headerId: h.id, description: `Traspaso · ${h.date}` }} />}
                    </div>
                    <div className="text-xs text-muted-foreground">{h.date} · {acc?.name} → {relAcc?.name}{Number(h.exchange_rate) !== 1 && ` · TC ${h.exchange_rate}`}</div>
                    {h.created_at && <div className="text-[10px] text-muted-foreground">Creado el {fmtCreated(h.created_at)}</div>}
                    {h.description && <div className="text-xs text-muted-foreground">{h.description}</div>}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-right">
                      <div className="text-sm text-expense">- {formatCurrency(Number(h.total_amount), acc?.currency ?? activeCurrency)}</div>
                      <div className="text-sm text-income">+ {formatCurrency(Number(h.total_amount) * Number(h.exchange_rate ?? 1), relAcc?.currency ?? activeCurrency)}</div>
                    </div>
                    {h.created_by === myId && (
                      <Button size="icon" variant="ghost" title="Eliminar"
                        onClick={() => setDeleting({ id: h.id, description: "Traspaso" })}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          }
          if ((h.type as any) === "iou") {
            const creditor = memMap.get((h as any).creditor_id ?? "")?.display_name ?? "—";
            const debtor = memMap.get((h as any).debtor_id ?? "")?.display_name ?? "—";
            const settled = !!(h as any).settled_at;
            return (
              <div key={h.id} className="p-4">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <div className="font-medium flex items-center gap-2 flex-wrap">
                      <Handshake className="h-4 w-4" /> Deuda: {creditor} → {debtor}
                      <span className={"rounded px-2 py-0.5 text-[10px] " + (settled ? "bg-muted text-muted-foreground" : "bg-[var(--warning-bg)] text-warning")}>
                        {settled ? `Saldado (${(h as any).settle_method === "forgive" ? "perdón" : "devuelto"})` : "Pendiente"}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">{h.date} · {acc?.name ?? ""}</div>
                    {h.description && <div className="text-xs text-muted-foreground">{h.description}</div>}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="font-semibold">{formatCurrency(Number(h.total_amount), acc?.currency ?? activeCurrency)}</div>
                    <Button size="sm" variant="outline" title="Ver en Deudas del hogar"
                      onClick={() => navigate({ to: "/iou" })}>
                      Deudas del hogar
                    </Button>
                    {h.created_by === myId && !settled && (
                      <Button size="icon" variant="ghost" title="Eliminar"
                        onClick={() => setDeleting({ id: h.id, description: `Deuda: ${creditor} → ${debtor}` })}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          }
          const isAdjustHeader = !isPeerPrivate && visibleLines.some((l) => adjustIds.has(l.category_id ?? ""));
          const isIncome = !isPeerPrivate && !isAdjustHeader && visibleLines.some((l) => catMap.get(l.category_id ?? "")?.type === "income");
          const isRefund = !!(h as any).is_refund;
          const positive = isIncome || isRefund || (isAdjustHeader && visibleLines.some((l) => catMap.get(l.category_id ?? "")?.type === "income"));
          const kindLabel = isAdjustHeader ? "ajuste" : isRefund ? "reembolso" : isIncome ? "ingreso" : "gasto";
          const hdrCatIds = new Set(
            visibleLines
              .map((l: any) => l.category_id)
              .filter((x: any): x is string => !!x),
          );
          const hdrCat = hdrCatIds.size === 1 ? catMap.get([...hdrCatIds][0] as string) : null;
          const hdrMulti = hdrCatIds.size > 1;
          const hdrMissing =
            !isPeerPrivate &&
            visibleLines.some((l: any) => l.owner_id === myId && !l.category_id);
          return (
            <div key={h.id} className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-medium flex items-center gap-2">
                    {isPeerPrivate ? `Movimiento privado de ${ownerName}` : (h.description || "(sin descripción)")}
                    {!isPeerPrivate && isAdjustHeader && (
                      <span className="rounded px-2 py-0.5 text-[10px] bg-muted text-foreground inline-flex items-center gap-1">
                        <Scale className="h-3 w-3" /> Ajuste
                      </span>
                    )}
                    {!isPeerPrivate && !isAdjustHeader && isRefund && (
                      <span className="rounded px-2 py-0.5 text-[10px] bg-income/15 text-income">Reembolso</span>
                    )}
                    {!isPeerPrivate && !isAdjustHeader && isIncome && !isRefund && (
                      <span className="rounded px-2 py-0.5 text-[10px] bg-income/15 text-income">Ingreso</span>
                    )}
                    {!isPeerPrivate && <SourceBadge source={(h as any).source} />}
                    {!isPeerPrivate && (h as any).verified_at && (
                      <VerifiedBadge label={verifyLabelsQ.data?.headers.get(h.id)} target={{ headerId: h.id, description: `${h.description || "(sin descripción)"} · ${h.date}` }} />
                    )}
                    {hdrMissing && (
                      <span className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] bg-[var(--warning-bg)] text-warning">
                        ⚠ Falta categoría
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                    <span>{h.date}</span>
                    {AccountChip}
                    {!isPeerPrivate && hdrCat && (
                      <span className="inline-flex items-center gap-1">
                        <span className="inline-block h-2 w-2 rounded-full" style={{ background: hdrCat.color }} />
                        {hdrCat.name}
                      </span>
                    )}
                    {!isPeerPrivate && hdrMulti && (
                      <span className="rounded px-1.5 py-0.5 text-[10px] bg-muted text-muted-foreground">
                        Múltiples categorías
                      </span>
                    )}
                    {!isPeerPrivate && <span>· {kindLabel}</span>}
                  </div>
                  {(() => {
                    // D1: el texto del banco, sólo si difiere de la descripción y nunca en un movimiento privado de la pareja.
                    const bankText = bankDescriptionToShow({
                      description: h.description,
                      bankDescription: (h as any).bank_description,
                      isPeerPrivate,
                    });
                    return bankText ? (
                      <div className="text-[11px] text-muted-foreground" title="Texto del banco">{bankText}</div>
                    ) : null;
                  })()}
                  {!isPeerPrivate && isRefund && refundLinksQ.data?.get(h.id) && (() => {
                    const orig = refundOriginalById.get(refundLinksQ.data!.get(h.id)!);
                    if (!orig) return null;
                    const origTotal = Number(orig.total_amount);
                    const pct = origTotal > 0 ? Math.round((Number(h.total_amount) / origTotal) * 100) : 0;
                    return (
                      <div className="text-[11px] text-muted-foreground">
                        Reembolso de {orig.description || "(sin descripción)"} · {orig.date} ·{" "}
                        {pct >= 100 ? "total" : `parcial ${pct}%`}
                      </div>
                    );
                  })()}
                  {!isPeerPrivate && !isRefund && (h as any).type === "regular" && (() => {
                    const remaining = refundRemainingQ.data?.get(h.id);
                    if (remaining == null) return null;
                    const total = Number(h.total_amount);
                    if (remaining >= total - 0.01) return null;
                    const refunded = Math.max(0, total - remaining);
                    const cur = (h as any).currency ?? acc?.currency ?? activeCurrency;
                    return (
                      <div className="text-[11px] text-muted-foreground">
                        Reembolsado {formatCurrency(refunded, cur)} de {formatCurrency(total, cur)}
                      </div>
                    );
                  })()}
                  {h.created_at && <div className="text-[10px] text-muted-foreground">Creado el {fmtCreated(h.created_at)}</div>}
                </div>
                <div className="flex items-center gap-2">
                  <div className={"text-right font-semibold " + (positive ? "text-income" : "")}>
                    {positive ? "+ " : ""}{formatCurrency(Number(h.total_amount), (h as any).currency ?? acc?.currency ?? activeCurrency)}
                  </div>
                  {!isPeerPrivate && h.created_by === myId && !isAdjustHeader && (
                    <Button size="icon" variant="ghost" title="Editar"
                      onClick={() => { setEditing({ header: h, lines: allLinesH }); setOpen(true); }}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                  )}
                  {hdrMissing && h.created_by !== myId && !isAdjustHeader && (
                    <Button size="sm" variant="outline" title="Categorizar mi parte"
                      onClick={() => setCategorizingMine({
                        header: h,
                        ownLines: visibleLines.filter((l: any) => l.owner_id === myId),
                        accountName: acc?.name ?? "",
                        accountCurrency: (h as any).currency ?? acc?.currency ?? activeCurrency,
                        isIncome,
                      })}>
                      Categorizar mi parte
                    </Button>
                  )}
                  {h.created_by === myId && !(h as any).is_income_record && (
                    <Button size="icon" variant="ghost" title="Eliminar"
                      onClick={() => setDeleting({ id: h.id, description: h.description || "(sin descripción)" })}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </div>
              </div>
              {isPeerPrivate ? (
                <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground"><Lock className="h-3 w-3" /> Movimiento privado de otro miembro</div>
              ) : (
                <div className="mt-2 space-y-1">
                  {visibleLines.map((l: any) => {
                    const c = catMap.get(l.category_id ?? "");
                    const m = memMap.get(l.owner_id);
                    const showOriginal = l.amount_original && l.currency_original && l.currency_original !== ((h as any).currency ?? acc?.currency ?? activeCurrency);
                    return (
                      <div key={l.id} className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-2">
                          <span className="inline-block h-2 w-2 rounded-full" style={{ background: c?.color ?? "#94a3b8" }} />
                          {c?.name ?? "—"} · <span className="text-xs text-muted-foreground">{m?.display_name}</span>
                          {l.visibility === "private" && <Lock className="h-3 w-3 text-muted-foreground" />}
                        </span>
                        <span className="text-right">
                          {formatCurrency(Number(l.amount), (h as any).currency ?? acc?.currency ?? activeCurrency)}
                          {showOriginal && (
                            <span className="block text-[10px] text-muted-foreground">
                              ({formatCurrency(Number(l.amount_original), l.currency_original)})
                            </span>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        {txQ.hasNextPage && (
          <div className="p-4 flex justify-center">
            <Button
              variant="outline"
              onClick={() => txQ.fetchNextPage()}
              disabled={txQ.isFetchingNextPage}
            >
              {txQ.isFetchingNextPage ? "Cargando…" : `Cargar más (${allHeaders.length} de ${visibleHeaders.length})`}
            </Button>
          </div>
        )}
      </Card>
      <TransactionFormDialog open={open} onOpenChange={(b) => { setOpen(b); if (!b) setEditing(null); }} editing={editing} />
      <TransferDialog open={openTransfer} onOpenChange={setOpenTransfer} />
      <IouDialog open={openIou} onOpenChange={setOpenIou} />
      <CcPurchaseDetailDialog open={!!ccDetail} onOpenChange={(b) => { if (!b) setCcDetail(null); }} purchase={ccDetail} />
      <CcRefundDialog open={!!refunding} onOpenChange={(b) => { if (!b) setRefunding(null); }} purchase={refunding} />
      <CategorizeMineDialog
        open={!!categorizingMine}
        onOpenChange={(b) => { if (!b) setCategorizingMine(null); }}
        header={categorizingMine?.header}
        ownLines={categorizingMine?.ownLines ?? []}
        accountName={categorizingMine?.accountName ?? ""}
        accountCurrency={categorizingMine?.accountCurrency ?? ""}
        isIncome={categorizingMine?.isIncome ?? false}
      />
      <BulkCategorizeDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        lines={bulkLines}
        partial={!!txQ.hasNextPage}
      />
      <AlertDialog open={!!unmarking} onOpenChange={(b) => { if (!b && !isUndoing) setUnmarking(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Quitar la marca del resumen?</AlertDialogTitle>
            <AlertDialogDescription>
              <b>{unmarking?.description}</b>
              <br />
              Deja de figurar como verificado por un resumen. No se borra el resumen ni cambia ningún monto:
              para volver a marcarlo, se sube de nuevo el resumen y se concilia.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isUndoing}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={isUndoing}
              onClick={async (e) => {
                e.preventDefault();
                if (!unmarking) return;
                setIsUndoing(true);
                try {
                  await unmarkVerifiedByStatement({ headerIds: unmarking.headerIds, purchaseIds: unmarking.purchaseIds });
                  toast.success("Marca del resumen quitada");
                  await qc.invalidateQueries();
                  setUnmarking(null);
                } catch (err) {
                  toastError(err);
                } finally {
                  setIsUndoing(false);
                }
              }}
            >
              {isUndoing ? "Quitando…" : "Quitar marca"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!unapplying} onOpenChange={(b) => { if (!b && !isUndoing) setUnapplying(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Desaplicar este pago?</AlertDialogTitle>
            <AlertDialogDescription>
              <b>{unapplying?.description}</b>
              <br />
              Saca las imputaciones hechas a mano sobre las cuotas y el pago vuelve a quedar «sin aplicar». El
              pago en sí no se borra ni cambia de monto; las cuotas que quedaron saldadas vuelven a quedar pendientes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isUndoing}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={isUndoing}
              onClick={async (e) => {
                e.preventDefault();
                if (!unapplying) return;
                setIsUndoing(true);
                try {
                  await unapplyCcPayment(unapplying.id);
                  toast.success("Pago desaplicado");
                  await qc.invalidateQueries();
                  setUnapplying(null);
                } catch (err) {
                  toastError(err);
                } finally {
                  setIsUndoing(false);
                }
              }}
            >
              {isUndoing ? "Desaplicando…" : "Desaplicar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!deleting} onOpenChange={(b) => { if (!b && !isDeleting) setDeleting(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este movimiento?</AlertDialogTitle>
            <AlertDialogDescription>
              <b>{deleting?.description}</b>
              <br />
              Esta acción es <b>irreversible</b>. Se borrarán las líneas asociadas y se
              recalcularán los saldos de las cuentas afectadas. Si el movimiento estaba
              ligado a una recurrencia, ésta volverá a estado <i>pendiente</i>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={isDeleting}
              onClick={async (e) => {
                e.preventDefault();
                if (!deleting) return;
                setIsDeleting(true);
                try {
                  if (deleting.id.startsWith("cc:")) {
                    await deleteCcPurchase(deleting.id.slice(3));
                  } else {
                    await deleteTransaction(deleting.id);
                  }
                  toast.success("Movimiento eliminado");
                  await qc.invalidateQueries();
                  setDeleting(null);
                } catch (err) {
                  toastError(err);
                } finally {
                  setIsDeleting(false);
                }
              }}
            >
              {isDeleting ? "Eliminando…" : "Eliminar definitivamente"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
